import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import {
  Wifi,
  AlertTriangle,
  RefreshCw,
  CheckCircle,
  CloudUpload
} from 'lucide-react';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';

// Duration to show "back online" message
const BACK_ONLINE_MESSAGE_DURATION = 3000;

interface OfflineIndicatorProps {
  pendingCount?: number;
  onRetrySync?: () => Promise<void>;
  className?: string;
}

/**
 * Offline indicator component that shows when user is offline
 * - Fixed banner at top of screen
 * - Yellow/warning color when offline
 * - Shows pending action count
 * - Auto-hides when back online with success message
 */
export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  pendingCount = 0,
  onRetrySync,
  className = '',
}) => {
  const isOnline = useOnlineStatus();
  const [showBanner, setShowBanner] = useState(false);
  const [showBackOnline, setShowBackOnline] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);

  // Handle coming back online
  useEffect(() => {
    if (isOnline && wasOffline) {
      // User just came back online
      setShowBackOnline(true);
      setShowBanner(true);

      // Hide the "back online" message after a delay
      const timer = setTimeout(() => {
        setShowBackOnline(false);
        // If no pending items, hide the banner completely
        if (pendingCount === 0) {
          setShowBanner(false);
        }
      }, BACK_ONLINE_MESSAGE_DURATION);

      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline, pendingCount]);

  // Track offline state
  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
      setShowBanner(true);
      setShowBackOnline(false);
    }
  }, [isOnline]);

  // Hide banner when online and no pending items
  useEffect(() => {
    if (isOnline && !showBackOnline && pendingCount === 0) {
      setShowBanner(false);
    } else if (!isOnline || pendingCount > 0) {
      setShowBanner(true);
    }
  }, [isOnline, showBackOnline, pendingCount]);

  // Handle manual sync retry
  const handleRetrySync = useCallback(async () => {
    if (!onRetrySync || isSyncing) return;

    setIsSyncing(true);
    setSyncSuccess(false);

    try {
      await onRetrySync();
      setSyncSuccess(true);

      // Reset success state after showing
      setTimeout(() => {
        setSyncSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Sync retry failed:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [onRetrySync, isSyncing]);

  // Animation variants
  const bannerVariants: Variants = {
    hidden: {
      y: -100,
      opacity: 0,
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 300,
        damping: 30,
      },
    },
    exit: {
      y: -100,
      opacity: 0,
      transition: {
        duration: 0.2,
      },
    },
  };

  // Determine banner state
  const getBannerState = () => {
    if (showBackOnline) {
      return 'online';
    }
    if (!isOnline) {
      return 'offline';
    }
    if (pendingCount > 0) {
      return 'pending';
    }
    return 'hidden';
  };

  const bannerState = getBannerState();

  // Get banner styles based on state
  const getBannerStyles = () => {
    switch (bannerState) {
      case 'online':
        return 'bg-green-500 text-white';
      case 'offline':
        return 'bg-yellow-500 text-yellow-900';
      case 'pending':
        return 'bg-blue-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  // Get banner icon
  const getBannerIcon = () => {
    switch (bannerState) {
      case 'online':
        return <CheckCircle className="w-5 h-5" />;
      case 'offline':
        return <AlertTriangle className="w-5 h-5" />;
      case 'pending':
        return <CloudUpload className="w-5 h-5" />;
      default:
        return <Wifi className="w-5 h-5" />;
    }
  };

  // Get banner message
  const getBannerMessage = () => {
    switch (bannerState) {
      case 'online':
        return "You're back online!";
      case 'offline':
        return pendingCount > 0
          ? `You're offline - ${pendingCount} booking${pendingCount !== 1 ? 's' : ''} will sync when connected`
          : "You're offline - Changes will sync when connected";
      case 'pending':
        return `${pendingCount} pending booking${pendingCount !== 1 ? 's' : ''} waiting to sync`;
      default:
        return '';
    }
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          className={`fixed top-0 left-0 right-0 z-50 safe-area-top ${className}`}
          variants={bannerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <div className={`${getBannerStyles()} shadow-md`}>
            <div className="max-w-7xl mx-auto px-4 py-2.5 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between gap-4">
                {/* Icon and Message */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="flex-shrink-0">{getBannerIcon()}</span>
                  <p className="text-sm font-medium truncate">
                    {getBannerMessage()}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Sync Button - show when online with pending items */}
                  {isOnline && pendingCount > 0 && onRetrySync && (
                    <button
                      onClick={handleRetrySync}
                      disabled={isSyncing}
                      className={`
                        flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md
                        transition-colors
                        ${
                          bannerState === 'pending'
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-white/20 hover:bg-white/30 text-white'
                        }
                        disabled:opacity-50 disabled:cursor-not-allowed
                      `}
                      aria-label="Sync pending bookings"
                    >
                      {isSyncing ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span className="hidden sm:inline">Syncing...</span>
                        </>
                      ) : syncSuccess ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          <span className="hidden sm:inline">Synced!</span>
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4" />
                          <span className="hidden sm:inline">Sync Now</span>
                        </>
                      )}
                    </button>
                  )}

                  {/* Connection Status Indicator */}
                  <div className="hidden sm:flex items-center gap-1.5">
                    <span
                      className={`w-2 h-2 rounded-full ${
                        isOnline ? 'bg-green-300' : 'bg-red-400'
                      } ${isOnline ? '' : 'animate-pulse'}`}
                    />
                    <span className="text-xs opacity-75">
                      {isOnline ? 'Online' : 'Offline'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Compact offline indicator for use in navigation or headers
 */
export const OfflineIndicatorCompact: React.FC<{
  pendingCount?: number;
  className?: string;
}> = ({ pendingCount = 0, className = '' }) => {
  const isOnline = useOnlineStatus();

  if (isOnline && pendingCount === 0) {
    return null;
  }

  return (
    <div
      className={`flex items-center gap-1.5 ${className}`}
      title={
        !isOnline
          ? 'You are offline'
          : `${pendingCount} pending booking${pendingCount !== 1 ? 's' : ''}`
      }
    >
      {!isOnline ? (
        <>
          <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
          <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">
            Offline
          </span>
        </>
      ) : pendingCount > 0 ? (
        <>
          <CloudUpload className="w-4 h-4 text-blue-500" />
          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
            {pendingCount}
          </span>
        </>
      ) : null}
    </div>
  );
};

export default OfflineIndicator;
